import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, Smartphone, Tag, PlayCircle } from "lucide-react";
import { DashboardMock } from "./DashboardMock";
import { EASE } from "./Reveal";
import { linkProps } from "@/lib/api";
import { trackEvent } from "@/lib/track";

const PriceBadge = ({ s }) => (
  <div className="absolute -right-2 -top-24 h-28 w-28 sm:-right-10 sm:-top-28 sm:h-36 sm:w-36" style={{ transform: "translateZ(90px)" }} data-testid="hero-price-badge">
    <svg viewBox="0 0 200 200" className="animate-spin-slow absolute inset-0 h-full w-full">
      <defs><path id="circ" d="M100 100 m-78 0 a78 78 0 1 1 156 0 a78 78 0 1 1 -156 0" /></defs>
      <circle cx="100" cy="100" r="98" fill="#FACC15" />
      <text className="font-mono-x" fontSize="15" fontWeight="700" letterSpacing="4" fill="#070B14">
        <textPath href="#circ">ONE TIME PAYMENT • NO MONTHLY FEES • </textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center text-[#070B14]">
      <span className="font-mono-x text-[10px] font-bold uppercase">Only</span>
      <span className="font-display text-3xl font-black leading-none sm:text-4xl">${s.price}</span>
    </div>
  </div>
);

const PhoneChip = () => (
  <div className="animate-floaty absolute -bottom-10 -left-4 w-36 rounded-[1.6rem] border-4 border-slate-800 bg-gradient-to-b from-blue-600 to-blue-900 p-3 shadow-2xl sm:-left-10 sm:w-40" style={{ transform: "translateZ(70px)" }}>
    <Smartphone className="h-5 w-5 text-cyan-200" />
    <p className="font-display mt-3 text-sm font-extrabold leading-tight text-white">More Traffic.<br />More Exposure.<br /><span className="text-[#FACC15]">More Growth.</span></p>
  </div>
);

const DemoLogins = ({ s }) => {
  const rows = [["Admin", s.demo_admin_user, s.demo_admin_pass, "admin"], ["Member", s.demo_member_user, s.demo_member_pass, "member"]].filter(([, u, p]) => u || p);
  if (!rows.length) return null;
  return (
    <div className="rounded-2xl border border-yellow-300 bg-white/90 px-4 py-2 text-xs text-slate-700 shadow-sm backdrop-blur" data-testid="hero-demo-logins">
      <p className="mb-1 font-mono-x text-[10px] font-bold uppercase tracking-wider text-slate-500">Demo login details</p>
      {rows.map(([label, u, p, id]) => (
        <p key={id} className="leading-relaxed" data-testid={`hero-demo-${id}-login`}>
          <span className="font-bold text-slate-900">{label}:</span>{" "}
          {u && <span className="font-mono-x">{u}</span>}{u && p && " / "}{p && <span className="font-mono-x">{p}</span>}
        </p>
      ))}
    </div>
  );
};

export const Hero = ({ s }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const lines = [s.hero_line1, s.hero_line2, s.hero_line3];
  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden bg-gradient-to-b from-[#E8F1FF] via-white to-white pb-28 pt-32 lg:min-h-[100svh] lg:pt-40">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <div className="absolute -right-40 -top-40 h-[640px] w-[640px] rounded-full bg-cyan-300/40 blur-[120px]" />
        <div className="absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-blue-400/25 blur-[120px]" />
        <div className="absolute bottom-0 right-1/3 h-[360px] w-[360px] rounded-full bg-yellow-200/60 blur-[120px]" />
      </motion.div>
      <div className="grid-lines absolute inset-0 opacity-60" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-20 px-5 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="inline-flex items-center gap-2.5 rounded-full border border-yellow-400 bg-[#FACC15] px-4 py-1.5 text-xs font-bold text-slate-900 shadow-sm sm:text-sm" data-testid="hero-announcement">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-600 opacity-75" /><span className="relative h-2 w-2 rounded-full bg-blue-600" /></span>
            {s.announcement}
          </motion.p>
          <h1 data-testid="hero-headline" className="font-display mt-7 text-[1.95rem] font-extrabold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {lines.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1.5">
                <motion.span className={`block ${i === 1 ? "text-gradient" : ""}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, ease: EASE, delay: 0.2 + i * 0.13 }}>{l}</motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.75 }} className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 md:text-lg" data-testid="hero-sub">{s.hero_sub}</motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.9 }} className="mt-10 flex flex-wrap items-center gap-4">
            <a {...linkProps(s.hero_cta_url, "#pricing")} data-testid="hero-pricing-link" className="group inline-flex items-center gap-2 rounded-full bg-blue-600 px-7 py-4 text-sm font-bold text-white shadow-[0_20px_40px_-15px_rgba(37,99,235,0.7)] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-blue-700">
              <Tag className="h-4 w-4" /> {s.hero_cta_text}
            </a>
            {s.demo_url && (
              <a {...linkProps(s.demo_url, "")} data-testid="hero-demo-button" onClick={() => trackEvent("demo_click")} className="group inline-flex items-center gap-2 rounded-full bg-[#FACC15] px-7 py-4 text-sm font-bold text-slate-900 shadow-[0_20px_40px_-15px_rgba(250,204,21,0.9)] transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-yellow-300">
                <PlayCircle className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" /> {s.demo_text}
              </a>
            )}
            {s.demo_url && <DemoLogins s={s} />}
          </motion.div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
            {["Free installation", "Free domain", "Lifetime hosting"].map((t) => (
              <span key={t} className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Check className="h-4 w-4 text-blue-600" />{t}</span>
            ))}
          </div>
        </div>
        <motion.div initial={{ opacity: 0, y: 80, scale: 0.94 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1.4, ease: EASE, delay: 0.35 }} className="relative mx-auto w-full max-w-[560px] lg:col-span-5">
          <motion.div style={{ y: imgY }} className="absolute -inset-x-4 -inset-y-12 overflow-hidden rounded-[2.5rem] shadow-2xl [clip-path:polygon(10%_0,100%_0,100%_90%,90%_100%,0_100%,0_10%)] sm:-inset-x-12">
            <img src="/img/hero.jpg" alt="Glowing globe of web traffic" className="h-full w-full object-cover" />
          </motion.div>
          <DashboardMock>
            <PriceBadge s={s} />
            <PhoneChip />
          </DashboardMock>
        </motion.div>
      </div>
    </section>
  );
};
